/**
 * The catalog's shared vocabulary.
 *
 * Three screens share these pieces so that a number means the same thing on all
 * of them. In particular `Headline` cannot render a figure without its `n`, and
 * `AspectRow` cannot render a descriptive aspect as a bare mean, because both
 * rules are enforced by the component's shape rather than by whoever is writing
 * the screen this week.
 *
 * All colour comes from tokens in `styles.css`. The only colours named here are
 * profile colours, which are user data.
 */

import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { Tile } from "../../components/Tile.tsx";
import { STAR_COUNT, formatStars, starLabel } from "../../../shared/stars.ts";
import { Link, useSyncState } from "../../main.tsx";
import type { SyncState } from "../../store/sync.ts";
import type { SessionParticipant, Stop } from "../../../shared/types.ts";
import { ATTRIBUTION, intrinsicWidth, sizedImageUrl } from "./bgg.ts";
import {
  nLabel,
  type AspectRollup,
  type Disagreement,
  type Distribution,
} from "./data.ts";

import "./shared.css";

// ---------------------------------------------------------------------------
// Box art
// ---------------------------------------------------------------------------

/**
 * What `BoxArt` actually needs, which is less than a `Game`.
 *
 * A search hit has art before it has a row in the database — the setup screen
 * shows the picked game's box before anything is written — so this is the three
 * fields rather than the whole record. `Game` satisfies it structurally, so
 * every existing caller is unchanged.
 */
export interface ArtSubject {
  title: string;
  thumb_url: string | null;
  image_url: string | null;
}

export interface BoxArtProps {
  game: ArtSubject;
  /** Rendered edge length in CSS pixels. */
  size: number;
  /** Only the one image above the fold should skip lazy loading. */
  eager?: boolean;
}


/**
 * Box art in a punched frame.
 *
 * Two things here are licence, not taste. The bytes are never modified, so the
 * image is `object-fit: contain` inside a reserved square rather than cropped
 * to fill it, and the only resizing is a different rendition requested from
 * BGG's own CDN. The square is reserved with `aspect-ratio` so the grid does
 * not reflow as art streams in — with fifty tiles on screen, reflow is the
 * difference between a shelf and a slot machine.
 */
export function BoxArt({ game, size, eager = false }: BoxArtProps): ReactNode {
  const [failed, setFailed] = useState(false);

  // The larger source whenever there is one. `image_url` is only ever filled by
  // a `/thing` lookup, so today this is always the thumbnail; when a token
  // changes that, the ceiling below lifts on its own and nothing else moves.
  const preferred = game.image_url ?? game.thumb_url;
  const ceiling = intrinsicWidth(preferred);

  // A URL that declares its rendition is already the only rendition there is —
  // the path is signed, and the parameters `sizedImageUrl` appends are ignored.
  // Asking anyway is not free: the service worker caches box art keyed on the
  // whole URL, so a per-call-site `?w=` turns one 64px file into one cache entry
  // and one download *per size on screen*. A result tile at 44px and the picked
  // tile at 72px would fetch the same bytes twice, against a 150-entry cache.
  const src = ceiling === null ? sizedImageUrl(preferred, size * 2, size * 2) : preferred;

  if (!src || failed) {
    return (
      <div className="pb-art pb-art--blank" style={{ "--pb-art-size": `${size}px` } as CSSProperties}>
        <span className="pb-art__initials" aria-hidden="true">
          {initials(game.title)}
        </span>
      </div>
    );
  }

  // Never rendered above its real resolution. `--pb-art-max` is a ceiling rather
  // than a size so that fluid callers — the shelf grid sizes its square from the
  // cell, not from `size` — stay fluid right up to the point where they would
  // start inventing pixels. A source with no declared rendition (a full-size URL
  // from a token, a manually entered image) sets no ceiling and behaves as before.
  //
  // The `width`/`height` attributes stay at `size`: `.pb-art__img` is 100% of its
  // frame, so they only supply the pre-load aspect ratio, and both values are
  // square either way. Enforcing the ceiling in CSS and in the attributes would
  // be one rule written twice.
  return (
    <div
      className="pb-art"
      style={
        {
          "--pb-art-size": `${size}px`,
          ...(ceiling === null ? {} : { "--pb-art-max": `${ceiling}px` }),
        } as CSSProperties
      }
    >
      <img
        className="pb-art__img"
        src={src}
        alt={`${game.title} box art`}
        width={size}
        height={size}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function initials(title: string): string {
  const words = title.split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "?";
  const second = words[1]?.charAt(0) ?? words[0]?.charAt(1) ?? "";
  return (first + second).toUpperCase();
}

/**
 * Names where the game data came from, in text and a link. See `ATTRIBUTION` in
 * `src/server/bgg.ts` for why there is no logo here.
 */
export function Attribution(): ReactNode {
  return (
    <p className="pb-attrib">
      <span>{ATTRIBUTION.text}</span>{" "}
      <a href={ATTRIBUTION.href} target="_blank" rel="noreferrer noopener">
        {ATTRIBUTION.linkText}
      </a>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

export interface HeadlineProps {
  dist: Distribution | null;
  /** Nights for a game, raters for a single session. They count different things. */
  unit: "night" | "rater";
  size?: "hero" | "normal";
  /** Shown in place of the number when there is nothing to show. */
  absent?: string;
  /**
   * Which figure this is. Required, because there are two of them now — the
   * replay number and how the evening went — and two unlabelled figures side by
   * side is worse than either alone.
   */
  label: string;
}

/**
 * A headline figure, in stars, never without its `n`.
 *
 * Stored values count half-stars on a 1–10 track, and nothing outside
 * `shared/stars.ts` should know that: `formatStars` is the only conversion, so
 * every screen either calls it or prints double what the rater set.
 *
 * Two callers now, and they are different questions. The replay number is typed
 * in directly and derived from nothing. The night's overall is the mean of each
 * seat's topics unless they overrode it — still never a roll-up of a *game's*
 * aspects, which is the thing this app does not do.
 */
export function Headline({
  dist,
  unit,
  size = "normal",
  absent = "Not rated yet",
  label,
}: HeadlineProps): ReactNode {
  if (!dist || dist.n === 0 || dist.mean === null) {
    return (
      <div className="pb-headline pb-headline--absent">
        <span className="pb-headline__label">{label}</span>
        {absent}
      </div>
    );
  }
  return (
    <div className="pb-headline" data-size={size}>
      <span className="pb-headline__label">{label}</span>
      <span className="pb-headline__value">{formatStars(dist.mean)}</span>
      <span className="pb-headline__scale">/{STAR_COUNT}</span>
      <span className="pb-headline__n">{nLabel(dist.n, unit)}</span>
      {dist.split && <SplitBadge spread={dist.spread} />}
    </div>
  );
}

export interface ScorePairProps {
  /** Desire to play again. Compulsory for a rater, so it is the one always shown. */
  headline: Distribution | null;
  /** How the evening went. Absent when nobody rated a topic on any of them. */
  overall: Distribution | null;
  unit: "night" | "rater";
  size?: "hero" | "normal";
  /** What to print in place of the headline. The overall has no such line. */
  absent?: string;
}

/**
 * The two figures a night carries, side by side wherever either is printed.
 *
 * They are allowed to disagree — that is the point of carrying both. One is
 * whether anybody wants a rematch; the other is how the evenings actually went.
 *
 * **The overall is dropped rather than rendered absent.** A tile in a grid is not
 * the place to explain a second empty slot, and a second "Not rated yet" beside
 * the first reads as a broken tile rather than as a fact about the night. That
 * rule lived at four call sites, in three different spellings of the same guard,
 * until it moved here.
 */
export function ScorePair({ headline, overall, unit, size, absent }: ScorePairProps): ReactNode {
  return (
    <div className="pb-scorepair">
      <Headline dist={headline} unit={unit} size={size} label="Play again" absent={absent} />
      {overall !== null && overall.n > 0 && (
        <Headline
          dist={overall}
          unit={unit}
          size={size}
          // Singular for a single night's raters, plural where the figure is a
          // game's nights pooled — which is exactly what `unit` already says.
          label={unit === "rater" ? "The night" : "The nights"}
        />
      )}
    </div>
  );
}

/** A split table is the interesting signal, so it is a badge and not a warning. */
export function SplitBadge({ spread }: { spread: number }): ReactNode {
  // In stars, like every other figure on screen. The stored spread is in
  // half-stars, so the badge used to claim twice the gap it was drawing.
  const apart = formatStars(spread);
  return (
    <span className="pb-badge" data-tone="pending" title={`${apart} stars apart`}>
      Split · {apart} apart
    </span>
  );
}

// ---------------------------------------------------------------------------
// Distributions
// ---------------------------------------------------------------------------

export interface StripProps {
  counts: ReadonlyMap<number, number>;
  stops: readonly Stop[];
  /**
   * Faint mean line behind the dots. Pass null for descriptive axes — the mean
   * of a bipolar Luck↔Strategy track describes no game anybody played.
   */
  mean?: number | null;
  /** Written stop labels under the track. Off where space is tight. */
  showLabels?: boolean;
}

/**
 * Where the group landed, as seated gems on a notched track.
 *
 * One column per stop, one dot per rater. The modal column is emphasised
 * because that is the honest summary of a discrete track; the mean, where it is
 * allowed at all, sits behind as a hairline rather than as the headline.
 */
export function Strip({ counts, stops, mean = null, showLabels = false }: StripProps): ReactNode {
  const ordered = [...stops].sort((a, b) => a.value - b.value);
  if (ordered.length === 0) return null;

  const bipolar = ordered.some((s) => s.value < 0);
  const peak = Math.max(1, ...ordered.map((s) => counts.get(s.value) ?? 0));
  const last = Math.max(ordered.length - 1, 1);
  const meanPos = mean === null ? null : fractionalIndex(ordered, mean);

  return (
    <div className="pb-strip" data-labelled={showLabels ? "true" : undefined}>
      <div className="pb-strip__track">
        {meanPos !== null && (
          <div
            className="pb-strip__mean"
            style={{ left: `${(meanPos / last) * 100}%` }}
            aria-hidden="true"
          />
        )}
        {ordered.map((stop) => {
          const count = counts.get(stop.value) ?? 0;
          return (
            <div
              className="pb-strip__col"
              key={stop.value}
              data-side={bipolar && stop.value < 0 ? "negative" : "positive"}
              data-modal={count > 0 && count === peak ? "true" : undefined}
            >
              <div className="pb-strip__dots">
                {Array.from({ length: count }, (_, i) => (
                  <span className="pb-strip__dot" key={i} />
                ))}
              </div>
              <div className="pb-strip__notch" />
              {showLabels && <div className="pb-strip__label">{stop.label}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Position of `value` on the stop ladder, interpolated between neighbours. */
function fractionalIndex(ordered: readonly Stop[], value: number): number {
  for (let i = 0; i < ordered.length - 1; i++) {
    const lo = ordered[i]!;
    const hi = ordered[i + 1]!;
    if (value >= lo.value && value <= hi.value) {
      const span = hi.value - lo.value;
      return span === 0 ? i : i + (value - lo.value) / span;
    }
  }
  return value < (ordered[0]?.value ?? 0) ? 0 : ordered.length - 1;
}

// ---------------------------------------------------------------------------
// Aspects
// ---------------------------------------------------------------------------

/**
 * One aspect's profile.
 *
 * Descriptive and evaluative aspects answer different questions, so they are
 * rendered differently rather than merely tagged: a descriptive axis reports
 * its modal stop label and nothing numeric, because a high Weight is not a good
 * Weight and a mean of a bipolar track is close to meaningless. An evaluative
 * one may show a mean, always beside its `n` and its spread.
 */
export function AspectRow({ rollup }: { rollup: AspectRollup }): ReactNode {
  const { profile, kind, stops, name } = rollup;
  const dist = profile.distribution;
  const evaluative = kind === "EVALUATIVE";

  return (
    <div className="pb-aspect" data-kind={kind.toLowerCase()}>
      <div className="pb-aspect__head">
        <span className="pb-aspect__name">{name}</span>
        <span className="pb-aspect__n">{nLabel(dist.n, "answer")}</span>
      </div>

      <div className="pb-aspect__verdict">
        {profile.modalLabel ?? "No answers"}
        {dist.split && <SplitBadge spread={dist.spread} />}
      </div>

      <Strip counts={dist.counts} stops={stops} mean={evaluative ? dist.mean : null} showLabels />

      <div className="pb-aspect__foot">
        {/* Stars, like everything else printed anywhere. The stored figure counts
            half-stars, so `toFixed(1)` on it read back double: a row everybody
            gave three stars reported "mean 6.0". */}
        {evaluative && dist.mean !== null && (
          <span>
            mean {formatStars(dist.mean)} · spread {formatStars(dist.spread)}
          </span>
        )}
        {!evaluative && dist.n > 0 && (
          <span>
            {starLabel(stops, dist.min) ?? formatStars(dist.min)} … {starLabel(stops, dist.max) ?? formatStars(dist.max)}
          </span>
        )}
        {profile.na > 0 && <span>{profile.na} said it doesn&rsquo;t apply</span>}
      </div>
    </div>
  );
}

/** The disagreement ranking, widest spread first. */
export function DisagreementList({ rows }: { rows: readonly Disagreement[] }): ReactNode {
  if (rows.length === 0) return null;
  const worst = rows[0]!;

  return (
    <div className="pb-disagree">
      <p className="pb-disagree__lede">
        {worst.profile.distribution.split
          ? `The table split hardest on ${worst.name}.`
          : `Closest to an argument: ${worst.name}.`}
      </p>
      <ol className="pb-disagree__list">
        {rows.map((row) => (
          <li key={row.id}>
            <span className="pb-disagree__name">{row.name}</span>
            <span
              className="pb-disagree__bar"
              style={{ "--pb-spread": String(row.profile.distribution.spread) } as CSSProperties}
              aria-hidden="true"
            />
            <span className="pb-disagree__num">
              {row.profile.distribution.spread} apart · {nLabel(row.profile.distribution.n, "answer")}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export function PlayerChips({ players }: { players: readonly SessionParticipant[] }): ReactNode {
  if (players.length === 0) return null;
  return (
    <div className="pb-players">
      {players.map((p) => (
        <span
          className="pb-players__chip"
          key={p.id}
          style={{ "--pb-chip": p.colour_snapshot } as CSSProperties}
        >
          {p.name_snapshot}
          {p.is_guest === 1 && <em> guest</em>}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

export function ScreenHeader({
  title,
  back,
  action,
}: {
  title: ReactNode;
  back?: { to: string; label: string };
  action?: ReactNode;
}): ReactNode {
  return (
    <header className="pb-head">
      {back && (
        <Link to={back.to} className="pb-head__back">
          ‹ {back.label}
        </Link>
      )}
      <div className="pb-head__row">
        <h1 className="pb-head__title">{title}</h1>
        {action}
      </div>
    </header>
  );
}

/**
 * What the status line says, as a plain function of the state so the wording can
 * be read back without a renderer.
 *
 * The order is worst-first, not alphabetical, and the one clause that had to be
 * added is the failed-transport one. `pending > 0` used to be checked before the
 * status, so a push the server had refused read as a neutral "3 unsynced" —
 * indistinguishable from three writes that simply had not gone out yet. It never
 * said an attempt had failed, and it never said another one was coming, which
 * left the queue looking like something the app was getting round to rather than
 * something that had gone wrong.
 */
export function syncWords(sync: SyncState): string {
  if (sync.conflicts > 0) return `${sync.conflicts} to resolve`;
  if (sync.deadLetters > 0) return `${sync.deadLetters} refused`;
  if (sync.status === "paused") return "signed out — work kept";
  if (sync.status === "offline") {
    return sync.pending > 0 ? `${sync.pending} waiting — offline` : "offline";
  }
  if (sync.status === "error") {
    // Named as this device's problem, and as one the app is already handling.
    // The transport retries on its own schedule now, so "try again" would be
    // asking for something that is happening anyway.
    return sync.pending > 0
      ? `${sync.pending} not sent — retrying`
      : "can't reach the server — retrying";
  }
  if (sync.pending > 0) return `${sync.pending} unsynced`;
  if (sync.status === "syncing") return "syncing";
  return "all synced";
}

export function syncTone(sync: SyncState): "ok" | "pending" | "error" {
  if (sync.conflicts > 0 || sync.deadLetters > 0 || sync.status === "error") return "error";
  if (sync.pending > 0 || sync.status === "paused" || sync.status === "offline") return "pending";
  return "ok";
}

/**
 * Unsynced work has to be visible, so this is a permanent status line and not a
 * toast. It stays one line: the group came here to look at games.
 *
 * It also carries the only route into settings, and therefore the only way to
 * sign out. That used to appear solely when a conflict had piled up, which meant
 * a healthy install had no way to reach its own account page — the link existed
 * but nothing in the app pointed at it. It goes here rather than in a nav bar
 * because this line is already on the three catalog screens and already the
 * app's quiet channel for anything to do with the server; a chrome bar would be
 * a second one saying less.
 */
export function SyncLine(): ReactNode {
  const sync = useSyncState();
  const needsAttention = sync.conflicts > 0 || sync.deadLetters > 0;

  return (
    <div className="pb-sync">
      <span className="pb-badge" data-tone={syncTone(sync)}>
        {syncWords(sync)}
      </span>
      <Link to="/settings" className="pb-sync__link">
        {needsAttention ? "Sort it out" : "Settings"}
      </Link>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}): ReactNode {
  return (
    <Tile sunk className="pb-blank">
      <h2 className="pb-blank__title">{title}</h2>
      {children}
      {action}
    </Tile>
  );
}

/** A small segmented control. Every option is a real, ≥44px target. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
}): ReactNode {
  return (
    <div className="pb-seg" role="group" aria-label={label}>
      <span className="pb-seg__label">{label}</span>
      <div className="pb-seg__opts">
        {options.map((opt) => (
          <button
            type="button"
            key={opt.value}
            className="pb-seg__opt"
            data-active={opt.value === value ? "true" : undefined}
            aria-pressed={opt.value === value}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
